// Single ChartDataPoint interface using epoch and quote directly
export interface ChartDataPoint {
  epoch: number;     // Unix timestamp in seconds
  quote: number;     // Current price
  symbol: string;    // Trading symbol
  precision?: number; // Decimal places from symbol metadata
  pip_size?: number;  // Legacy alias kept for compatibility
}

export type ChartDataInputPoint = {
  epoch?: number;
  epoch_ms?: number;
  time?: number;
  quote?: number;
  price?: number;
  symbol?: string;
  precision?: number;
  pip_size?: number;
  pipSize?: number;
};

export class ChartDataPointPool {
  private freeList: ChartDataPoint[] = [];
  private allocated = 0;
  private readonly maxPoolSize: number;

  constructor(maxPoolSize: number = 10000) {
    this.maxPoolSize = maxPoolSize;
  }

  acquire(epoch: number, quote: number, symbol: string, precision: number): ChartDataPoint {
    let point = this.freeList.pop();
    if (!point) {
      point = { epoch, quote, symbol, precision, pip_size: precision };
      this.allocated += 1;
    } else {
      point.epoch = epoch;
      point.quote = quote;
      point.symbol = symbol;
      point.precision = precision;
      point.pip_size = precision;
    }
    return point;
  }

  release(point: ChartDataPoint): void {
    if (this.freeList.length >= this.maxPoolSize) return;
    this.freeList.push(point);
  }

  preAllocate(count: number): void {
    const target = Math.min(this.maxPoolSize, this.freeList.length + count);
    while (this.freeList.length < target) {
      this.freeList.push({ epoch: 0, quote: 0, symbol: "", precision: 0, pip_size: 0 });
      this.allocated += 1;
    }
  }

  getStats(): { allocated: number; free: number } {
    return { allocated: this.allocated, free: this.freeList.length };
  }
}

export const dataPointPool = new ChartDataPointPool(20000);

export function normalizeChartDataPoint(
  point: ChartDataInputPoint | ChartDataPoint,
  fallbackSymbol = "",
  fallbackPipSize = 3,
): ChartDataPoint {
  const raw = point as ChartDataInputPoint;
  const rawEpoch = typeof raw.epoch === "number"
    ? raw.epoch
    : typeof raw.epoch_ms === "number"
      ? raw.epoch_ms
    : typeof raw.time === "number"
      ? raw.time
      : Date.now() / 1000;
  const epoch = rawEpoch > 10_000_000_000 ? Math.floor(rawEpoch / 1000) : Math.floor(rawEpoch);
  const quote = typeof raw.quote === "number"
    ? raw.quote
    : typeof raw.price === "number"
      ? raw.price
      : 0;
  const symbol = typeof raw.symbol === "string" && raw.symbol.trim() ? raw.symbol : fallbackSymbol;
  
  // ===== ENFORCE PRECISION =====
  // Prefer symbol precision; keep legacy pip fields as fallback.
  const precision = typeof raw.precision === "number"
    ? raw.precision
    : typeof raw.pip_size === "number"
      ? raw.pip_size
      : typeof raw.pipSize === "number"
        ? raw.pipSize
        : fallbackPipSize;
  
  // Second try: if symbol is known, use its precision from a mapping
  // (This will be set by the frontend when feeding data)
  
  // ===== ROUND THE QUOTE TO THE CORRECT PRECISION =====
  const roundedQuote = Number(quote.toFixed(precision));

  return {
    epoch,
    quote: roundedQuote,
    symbol,
    precision,
    pip_size: precision,
  };
}

export function normalizeChartDataPoints(
  points: Array<ChartDataInputPoint | ChartDataPoint>,
  fallbackSymbol = "",
  fallbackPipSize = 3,
): ChartDataPoint[] {
  return points
    .map((point) => normalizeChartDataPoint(point, fallbackSymbol, fallbackPipSize))
    .sort((a, b) => a.epoch - b.epoch);
}

function lowerBoundEpoch(data: { epoch: number }[], targetSec: number): number {
  let lo = 0;
  let hi = data.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (data[mid].epoch < targetSec) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

function upperBoundEpoch(data: { epoch: number }[], targetSec: number): number {
  let lo = 0;
  let hi = data.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (data[mid].epoch <= targetSec) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

// Returns [from, to) index bounds for the visible window without allocating.
export function getVisibleBounds<T extends { epoch: number }>(
  data: T[],
  timeStartMs: number,
  timeEndMs: number,
): { from: number; to: number } | null {
  if (!data.length) return null;
  const startSec = Math.floor(timeStartMs / 1000);
  const endSec = Math.ceil(timeEndMs / 1000);
  const from = lowerBoundEpoch(data, startSec);
  const to = upperBoundEpoch(data, endSec);
  if (to <= from) return null;
  return { from, to };
}

// Filters series data to the active visible time window.
export function getVisibleData<T extends { epoch: number }>(
  data: T[],
  timeStartMs: number,
  timeEndMs: number,
): T[] {
  const bounds = getVisibleBounds(data, timeStartMs, timeEndMs);
  if (!bounds) return [];
  return data.slice(bounds.from, bounds.to);
}

// Get the latest data timestamp in milliseconds
export function getLatestDataTime(data: ChartDataPoint[]): number {
  if (!data || data.length === 0) return Date.now();
  return data[data.length - 1].epoch * 1000;
}

// Get the earliest data timestamp in milliseconds
export function getEarliestDataTime(data: ChartDataPoint[]): number {
  if (!data || data.length === 0) return Date.now();
  return data[0].epoch * 1000;
}

// Get the time range of the data
export function getDataTimeRange(data: ChartDataPoint[]): { start: number; end: number } {
  if (!data || data.length === 0) {
    const now = Date.now();
    return { start: now, end: now };
  }
  return {
    start: data[0].epoch * 1000,
    end: data[data.length - 1].epoch * 1000,
  };
}
